import { motion } from "framer-motion";
import { Target, Users, Sprout, Globe, TrendingUp, Heart } from "lucide-react";

const Mission = () => {
  const programmes = [
    {
      title: "Sustainable Agriculture, Food & Livelihood (SAFAL)",
      icon: Sprout,
      points: [
        "Promotes chemical-free, climate-resilient natural farming",
        "Focuses on soil health management, water conservation, and crop diversification",
        "Partners with Gram Panchayats, SHGs, and agriculture departments",
        "Promotes nutritional awareness and millet cultivation",
      ],
    },
    {
      title: "Reviving Rainfed Seed Systems (RRSS)",
      icon: Globe,
      points: [
        "Initiated the 'Chamba Beej Khojo Abhiyan' to identify local seed saviours",
        "Organizes Beej Melas (seed fairs) and farmer training",
        "Establishes community seed banks and trial plots",
        "Conserves indigenous biodiversity and revives local farming wisdom",
      ],
    },
    {
      title: "Disaster Relief & Community Resilience (DRCR)",
      icon: Heart,
      points: [
        "Provides emergency relief during floods and landslides",
        "Engages local administration and SHGs for quick response",
        "Builds awareness in disaster-prone areas",
        "Links relief with long-term climate resilience strategies",
      ],
    },
  ];

  const achievements = [
    "710 farmers mobilized to reduce chemical farming",
    "2 traditional seed banks established",
    "147 farmers exchanged seeds",
    "20 champion farmers cultivating traditional seeds",
    "191 women trained in millet-based recipes",
    "Healthy millet tea introduced to 270 school students",
    "Flood relief support to 500+ farming households",
  ];

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative h-[50vh] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1701153758624-4b3d352bcfbd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwxfHxjYWxtJTIwaGltYWxheWFzJTIwbW91bnRhaW4lMjBsYW5kc2NhcGUlMjBkaGFyYW1zaGFsYXxlbnwwfHx8fDE3NzQ3NjM2OTd8MA&ixlib=rb-4.1.0&q=85')",
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 to-black/50" />
        </div>
        <div className="relative z-10 text-center max-w-4xl mx-auto px-4">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl sm:text-5xl lg:text-6xl font-heading font-semibold text-white mb-6"
            style={{ textShadow: "0 2px 10px rgba(0,0,0,0.3)" }}
          >
            Our Mission
          </motion.h1>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-[#2B5C45] text-white rounded-2xl p-8 lg:p-12"
            >
              <Target className="h-12 w-12 mb-6" />
              <h2 className="text-3xl font-heading font-medium mb-4">Vision</h2>
              <p className="text-base leading-relaxed opacity-90">
                A world where every person—regardless of caste, creed, gender, or background—is empowered to access all resources, information, livelihood and support systems to lead a healthy, hunger-free, and fulfilling life.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-[#8C9A74] text-white rounded-2xl p-8 lg:p-12"
            >
              <Users className="h-12 w-12 mb-6" />
              <h2 className="text-3xl font-heading font-medium mb-4">Mission</h2>
              <p className="text-base leading-relaxed opacity-90">
                Empowering small and marginal farming communities including women and youth by enhancing their capacities and knowledge, enabling them to take collective actions for climate-resilient sustainable farming, biodiversity conservation, livelihoods enhancement, and the overall empowerment of marginalized communities.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Key Programmes */}
      <section className="py-24 bg-[#F9F8F6]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-medium text-[#1C221F] mb-4">
              Key Programmes
            </h2>
            <p className="text-base text-[#515E58] max-w-2xl mx-auto">
              Comprehensive initiatives for sustainable development
            </p>
          </motion.div>

          <div className="space-y-8">
            {programmes.map((programme, index) => (
              <motion.div
                key={programme.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-xl p-8 shadow-sm border border-[#E4E2DC]/40"
              >
                <div className="flex items-start space-x-4">
                  <programme.icon className="h-10 w-10 text-[#2B5C45] flex-shrink-0" />
                  <div className="flex-1">
                    <h3 className="text-xl sm:text-2xl font-heading font-medium text-[#1C221F] mb-4">
                      {programme.title}
                    </h3>
                    <ul className="space-y-2">
                      {programme.points.map((point, i) => (
                        <li key={i} className="flex items-start space-x-3">
                          <div className="h-2 w-2 rounded-full bg-[#2B5C45] flex-shrink-0 mt-2" />
                          <span className="text-base text-[#515E58]">{point}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Achievements */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <TrendingUp className="h-12 w-12 text-[#2B5C45] mx-auto mb-4" />
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-medium text-[#1C221F] mb-4">
              Key Achievements
            </h2>
            <p className="text-base text-[#515E58] max-w-2xl mx-auto">
              Measurable impact on communities
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {achievements.map((achievement, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="flex items-start space-x-3 p-6 bg-[#F9F8F6] rounded-xl border border-[#E4E2DC]/40"
              >
                <div className="h-6 w-6 rounded-full bg-[#2B5C45] flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-white text-xs font-bold">{index + 1}</span>
                </div>
                <p className="text-base text-[#515E58]">{achievement}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Mission;
